import { components } from "types/declarations/api";
import {
	LineItemDetails,
	LineItemDetailsItemContainer,
	LineItemDetailsItemLabel,
	LineItemDetailsNumber,
	LineItemDetailsSkuContainer,
	LineItemDetailsSkuDescription,
	LineItemDetailsSkuLabel,
	LineItemDetailsSkuNumberContainer,
	LineItemModificationsContainer,
	LineItemModificationsNumber,
	LineItemModificationsPricesContainer,
	LineItemModificationsSkuDescription,
	LineItemModificationsSkuLabel,
	LineItemModificationsSkuNumberContainer,
	LineItemWrapper
} from "./ReviewTruckLineItemCard.styles";
import { DoorStyleText, FinishText, QuantityText, UnavailableDataPlaceholderText } from "constants/text";
import { formatPrice } from "utils/string";

interface ReviewTruckDetailsProps {
	lineItemNumber: number;
	item: components["schemas"]["PendingLineItemViewModel"];
	style: string;
	finish: string;
}

const ReviewTruckLineItemCard = ({ lineItemNumber, item, style, finish }: ReviewTruckDetailsProps) => {
	return (
		<LineItemWrapper data-testid="trucks-review-truck-details">
			<LineItemDetails>
				<LineItemDetailsSkuNumberContainer>
					<LineItemDetailsNumber data-testid={`line-item-number-${lineItemNumber}`}>
						{lineItemNumber}
					</LineItemDetailsNumber>
					<LineItemDetailsSkuContainer>
						<LineItemDetailsSkuLabel>{item?.sku ?? UnavailableDataPlaceholderText}</LineItemDetailsSkuLabel>
						<LineItemDetailsSkuDescription data-testid={`line-item-description-${lineItemNumber}`}>
							{item?.description ?? UnavailableDataPlaceholderText}
						</LineItemDetailsSkuDescription>
					</LineItemDetailsSkuContainer>
				</LineItemDetailsSkuNumberContainer>
				<LineItemDetailsItemContainer>
					<LineItemDetailsItemLabel>{DoorStyleText}</LineItemDetailsItemLabel>
					<LineItemDetailsNumber data-testid={`line-item-style-${lineItemNumber}`}>
						{style ?? UnavailableDataPlaceholderText}
					</LineItemDetailsNumber>
				</LineItemDetailsItemContainer>
				<LineItemDetailsItemContainer>
					<LineItemDetailsItemLabel>{FinishText}</LineItemDetailsItemLabel>
					<LineItemDetailsNumber data-testid={`line-item-finish-${lineItemNumber}`}>
						{finish ?? UnavailableDataPlaceholderText}
					</LineItemDetailsNumber>
				</LineItemDetailsItemContainer>
				<LineItemDetailsItemContainer>
					<LineItemDetailsItemLabel>{QuantityText}</LineItemDetailsItemLabel>
					<LineItemDetailsNumber data-testid={`line-item-quantity-${lineItemNumber}`}>
						{item?.quantityOrdered ?? UnavailableDataPlaceholderText}
					</LineItemDetailsNumber>
				</LineItemDetailsItemContainer>
				<LineItemDetailsItemContainer>
					<LineItemDetailsItemLabel>List Price</LineItemDetailsItemLabel>
					<LineItemDetailsNumber data-testid={`line-item-list-price-${lineItemNumber}`}>
						{formatPrice(item?.listPrice) ?? UnavailableDataPlaceholderText}
					</LineItemDetailsNumber>
				</LineItemDetailsItemContainer>
				<LineItemDetailsItemContainer>
					<LineItemDetailsItemLabel>Extended Price</LineItemDetailsItemLabel>
					<LineItemDetailsNumber data-testid={`line-item-extended-price-${lineItemNumber}`}>
						{formatPrice(item?.extendedPrice) ?? UnavailableDataPlaceholderText}
					</LineItemDetailsNumber>
				</LineItemDetailsItemContainer>
			</LineItemDetails>
			{item?.modifications?.map((modification, index) => (
				<LineItemModificationsContainer key={modification.id}>
					<LineItemModificationsSkuNumberContainer>
						<LineItemModificationsNumber data-testid={`modification-number-${lineItemNumber}-${index + 1}`}>
							{lineItemNumber}.{index + 1}
						</LineItemModificationsNumber>
						<LineItemModificationsSkuLabel>
							{modification.sku ?? UnavailableDataPlaceholderText}
						</LineItemModificationsSkuLabel>
						<LineItemModificationsSkuDescription
							data-testid={`modification-description-${lineItemNumber}-${index + 1}`}
						>
							{modification.description ?? UnavailableDataPlaceholderText}
						</LineItemModificationsSkuDescription>
					</LineItemModificationsSkuNumberContainer>
					<LineItemModificationsPricesContainer>
						<LineItemDetailsItemContainer>
							<LineItemDetailsNumber data-testid={`modification-price-${lineItemNumber}-${index + 1}`}>
								{formatPrice(modification.netPrice ?? modification.listPrice) ??
									UnavailableDataPlaceholderText}
							</LineItemDetailsNumber>
						</LineItemDetailsItemContainer>
						<LineItemDetailsItemContainer>
							<LineItemDetailsNumber
								data-testid={`modification-extended-price-${lineItemNumber}-${index + 1}`}
							>
								{formatPrice(
									(modification.netPrice ?? modification.listPrice ?? 0) *
										(item?.quantityOrdered ?? 0)
								) ?? UnavailableDataPlaceholderText}
							</LineItemDetailsNumber>
						</LineItemDetailsItemContainer>
					</LineItemModificationsPricesContainer>
				</LineItemModificationsContainer>
			))}
		</LineItemWrapper>
	);
};

export default ReviewTruckLineItemCard;
